import React, { useEffect, useMemo, useRef, useState } from "react";
import "./LabirintoEquilibrioGame.css";
import { isTabletWidthOnly as isTablet } from "../shared/device";
import { createMazeBlueprint as generateMazeBlueprint } from "./mazeGenerator";

type GameStatus = "idle" | "playing" | "success" | "failed";

interface Point {
  x: number;
  y: number;
}

interface Cell {
  row: number;
  col: number;
}

interface MazeCell {
  u: boolean;
  d: boolean;
  l: boolean;
  r: boolean;
  visited: boolean;
}

interface WallLine {
  key: string;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

interface MazeBlueprint {
  rows: number;
  cols: number;
  grid: MazeCell[][];
  entryCell: Cell;
  exitCell: Cell;
  wallLines: WallLine[];
}

interface LabirintoEquilibrioGameProps {
  onBackToHome: () => void;
}

const PRIZE_IMAGE_PATH = "/images/products/mel.png";
const ROUND_DURATION_SECONDS = 20;
const MAX_CRASHES = 3;
const POINTER_REACH_RADIUS_MULTIPLIER = 0.62;
const POINTER_CELL_COMMIT_RADIUS_MULTIPLIER = 0.58;
const CRASH_REJOIN_RADIUS_MULTIPLIER = 2;
const WALL_STROKE_WIDTH_MULTIPLIER = 0.05;
const MIN_WALL_STROKE_WIDTH = 2;
const TRACE_STROKE_WIDTH_MULTIPLIER = 0.68;
// Negative tolerance expressed as a fraction of the trace stroke width so
// leniency stays proportional across tablets and large vertical TVs.
const WALL_COLLISION_TOLERANCE_TRACE_RATIO = -0.13;
const COLLISION_COOLDOWN_MS = 320;
const MAZE_ROWS = 8;
const MAZE_COLUMNS = 11;
const FIXED_MAZE_SEED = "labirinto-v1-stable-easy";

function getDistance(firstPoint: Point, secondPoint: Point) {
  const deltaX = firstPoint.x - secondPoint.x;
  const deltaY = firstPoint.y - secondPoint.y;
  return Math.hypot(deltaX, deltaY);
}

function getPointToSegmentDistance(
  point: Point,
  segmentStart: Point,
  segmentEnd: Point,
) {
  const segmentX = segmentEnd.x - segmentStart.x;
  const segmentY = segmentEnd.y - segmentStart.y;
  const segmentLengthSquared = segmentX * segmentX + segmentY * segmentY;

  if (segmentLengthSquared === 0) {
    return getDistance(point, segmentStart);
  }

  const pointVectorX = point.x - segmentStart.x;
  const pointVectorY = point.y - segmentStart.y;
  const projectionRatio =
    (pointVectorX * segmentX + pointVectorY * segmentY) / segmentLengthSquared;
  const clampedRatio = Math.max(0, Math.min(1, projectionRatio));

  const projectionPoint = {
    x: segmentStart.x + segmentX * clampedRatio,
    y: segmentStart.y + segmentY * clampedRatio,
  };

  return getDistance(point, projectionPoint);
}

function getPointerPosition(
  event: React.PointerEvent<HTMLElement>,
  container: HTMLElement,
  mazeSize: { width: number; height: number },
) {
  const bounds = container.getBoundingClientRect();
  const rawX = event.clientX - bounds.left;
  const rawY = event.clientY - bounds.top;
  const safeWidth = Math.max(1, bounds.width);
  const safeHeight = Math.max(1, bounds.height);
  const scaleX = mazeSize.width / safeWidth;
  const scaleY = mazeSize.height / safeHeight;

  return {
    x: rawX * scaleX,
    y: rawY * scaleY,
  };
}

function getCellCenter(
  row: number,
  col: number,
  cellWidth: number,
  cellHeight: number,
) {
  return {
    x: col * cellWidth + cellWidth / 2,
    y: row * cellHeight + cellHeight / 2,
  };
}

function isSameCell(firstCell: Cell, secondCell: Cell) {
  return firstCell.row === secondCell.row && firstCell.col === secondCell.col;
}

function getSnappedMazeSize(availableWidth: number, availableHeight: number) {
  const cellSize = Math.max(
    1,
    Math.floor(
      Math.min(availableWidth / MAZE_COLUMNS, availableHeight / MAZE_ROWS),
    ),
  );

  return {
    width: cellSize * MAZE_COLUMNS,
    height: cellSize * MAZE_ROWS,
  };
}

function getConnectedNeighborCells(grid: MazeCell[][], cell: Cell) {
  const rows = grid.length;
  const cols = grid[0].length;
  const current = grid[cell.row][cell.col];
  const neighbors: Cell[] = [];

  if (current.u && cell.row > 0) {
    neighbors.push({ row: cell.row - 1, col: cell.col });
  }
  if (current.d && cell.row < rows - 1) {
    neighbors.push({ row: cell.row + 1, col: cell.col });
  }
  if (current.l && cell.col > 0) {
    neighbors.push({ row: cell.row, col: cell.col - 1 });
  }
  if (current.r && cell.col < cols - 1) {
    neighbors.push({ row: cell.row, col: cell.col + 1 });
  }

  return neighbors;
}

export default function LabirintoEquilibrioGame({
  onBackToHome,
}: LabirintoEquilibrioGameProps) {
  const mazeRef = useRef<HTMLDivElement>(null);
  const pointerActiveRef = useRef(false);
  const lastCrashPointRef = useRef<Point | null>(null);
  const cellSizeRef = useRef({ width: 1, height: 1 });
  const crashFlashTimeoutRef = useRef<number | null>(null);
  const crashCountRef = useRef(0);
  const lastCrashAtRef = useRef(0);
  const traceCellsRef = useRef<Cell[]>([]);

  const [mazeBlueprint] = useState<MazeBlueprint>(() =>
    generateMazeBlueprint({
      rows: MAZE_ROWS,
      cols: MAZE_COLUMNS,
      seed: FIXED_MAZE_SEED,
    }),
  );
  const [status, setStatus] = useState<GameStatus>("idle");
  const [timeLeft, setTimeLeft] = useState(ROUND_DURATION_SECONDS);
  const [crashCount, setCrashCount] = useState(0);
  const [hasRoundStarted, setHasRoundStarted] = useState(false);
  const [traceCells, setTraceCells] = useState<Cell[]>([]);
  const [isCrashFlashing, setIsCrashFlashing] = useState(false);
  const [mazeSize, setMazeSize] = useState({ width: 520, height: 520 });

  const cellSize = useMemo(
    () => ({
      width: mazeSize.width / mazeBlueprint.cols,
      height: mazeSize.height / mazeBlueprint.rows,
    }),
    [mazeBlueprint.cols, mazeBlueprint.rows, mazeSize.height, mazeSize.width],
  );

  const startPoint = useMemo(
    () =>
      getCellCenter(
        mazeBlueprint.entryCell.row,
        mazeBlueprint.entryCell.col,
        cellSize.width,
        cellSize.height,
      ),
    [
      cellSize.height,
      cellSize.width,
      mazeBlueprint.entryCell.col,
      mazeBlueprint.entryCell.row,
    ],
  );

  const wallStrokeWidth = useMemo(
    () =>
      Math.max(
        MIN_WALL_STROKE_WIDTH,
        Math.min(cellSize.width, cellSize.height) *
          WALL_STROKE_WIDTH_MULTIPLIER,
      ),
    [cellSize.height, cellSize.width],
  );

  const wallLines = useMemo<WallLine[]>(() => {
    const halfStrokeWidth = wallStrokeWidth / 2;

    const normalizeX = (x: number) => {
      if (x === 0) return halfStrokeWidth;
      if (x === mazeSize.width) return mazeSize.width - halfStrokeWidth;
      return x;
    };

    const normalizeY = (y: number) => {
      if (y === 0) return halfStrokeWidth;
      if (y === mazeSize.height) return mazeSize.height - halfStrokeWidth;
      return y;
    };

    return mazeBlueprint.wallLines.map((line) => {
      const x1 = line.x1 * cellSize.width;
      const y1 = line.y1 * cellSize.height;
      const x2 = line.x2 * cellSize.width;
      const y2 = line.y2 * cellSize.height;

      return {
        key: line.key,
        x1: normalizeX(x1),
        y1: normalizeY(y1),
        x2: normalizeX(x2),
        y2: normalizeY(y2),
      };
    });
  }, [
    cellSize.height,
    cellSize.width,
    mazeBlueprint.wallLines,
    mazeSize.height,
    mazeSize.width,
    wallStrokeWidth,
  ]);

  const tracePoints = useMemo<Point[]>(
    () =>
      traceCells.map((cell) =>
        getCellCenter(cell.row, cell.col, cellSize.width, cellSize.height),
      ),
    [cellSize.height, cellSize.width, traceCells],
  );

  const traceStrokeWidth = useMemo(
    () =>
      Math.max(
        10,
        Math.min(cellSize.width, cellSize.height) *
          TRACE_STROKE_WIDTH_MULTIPLIER,
      ),
    [cellSize.height, cellSize.width],
  );

  const wallCollisionRadius = useMemo(
    () =>
      wallStrokeWidth / 2 +
      traceStrokeWidth / 2 +
      traceStrokeWidth * WALL_COLLISION_TOLERANCE_TRACE_RATIO,
    [traceStrokeWidth, wallStrokeWidth],
  );

  useEffect(() => {
    const mazeElement = mazeRef.current;
    if (!mazeElement) return;

    const observer = new ResizeObserver(() => {
      const width = Math.max(1, mazeElement.clientWidth);
      const height = Math.max(1, mazeElement.clientHeight);
      setMazeSize(getSnappedMazeSize(width, height));
    });

    observer.observe(mazeElement);

    const width = Math.max(1, mazeElement.clientWidth);
    const height = Math.max(1, mazeElement.clientHeight);
    setMazeSize(getSnappedMazeSize(width, height));

    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    cellSizeRef.current = cellSize;
  }, [cellSize]);

  useEffect(() => {
    crashCountRef.current = crashCount;
  }, [crashCount]);

  useEffect(() => {
    traceCellsRef.current = traceCells;
  }, [traceCells]);

  useEffect(() => {
    return () => {
      if (crashFlashTimeoutRef.current !== null) {
        window.clearTimeout(crashFlashTimeoutRef.current);
      }
    };
  }, []);

  const resetAttempt = () => {
    pointerActiveRef.current = false;
    lastCrashPointRef.current = null;
    setTraceCells([]);
    traceCellsRef.current = [];
    setTimeLeft(ROUND_DURATION_SECONDS);
    setCrashCount(0);
    crashCountRef.current = 0;
    lastCrashAtRef.current = 0;
    setHasRoundStarted(false);
    setIsCrashFlashing(false);
    setStatus("idle");
  };

  const markSuccess = () => {
    pointerActiveRef.current = false;
    lastCrashPointRef.current = null;

    setTraceCells((currentCells) => {
      if (currentCells.length === 0) {
        return [mazeBlueprint.entryCell, mazeBlueprint.exitCell];
      }

      const lastCell = currentCells[currentCells.length - 1];
      if (isSameCell(lastCell, mazeBlueprint.exitCell)) {
        return currentCells;
      }

      return [...currentCells, mazeBlueprint.exitCell];
    });

    if (traceCellsRef.current.length === 0) {
      traceCellsRef.current = [mazeBlueprint.entryCell, mazeBlueprint.exitCell];
    } else {
      const lastTraceCell =
        traceCellsRef.current[traceCellsRef.current.length - 1];
      if (!isSameCell(lastTraceCell, mazeBlueprint.exitCell)) {
        traceCellsRef.current = [
          ...traceCellsRef.current,
          mazeBlueprint.exitCell,
        ];
      }
    }

    setStatus("success");
  };

  const triggerCrashFlash = () => {
    setIsCrashFlashing(true);

    if (crashFlashTimeoutRef.current !== null) {
      window.clearTimeout(crashFlashTimeoutRef.current);
    }

    crashFlashTimeoutRef.current = window.setTimeout(() => {
      setIsCrashFlashing(false);
      crashFlashTimeoutRef.current = null;
    }, 220);
  };

  const registerCrash = (crashPoint: Point) => {
    const now = Date.now();
    if (now - lastCrashAtRef.current < COLLISION_COOLDOWN_MS) {
      return;
    }
    lastCrashAtRef.current = now;

    const nextCrashCount = crashCountRef.current + 1;
    crashCountRef.current = nextCrashCount;
    setCrashCount(nextCrashCount);
    triggerCrashFlash();
    pointerActiveRef.current = false;
    lastCrashPointRef.current = crashPoint;

    if (nextCrashCount >= MAX_CRASHES) {
      const resetTrace = [mazeBlueprint.entryCell];
      setTraceCells(resetTrace);
      traceCellsRef.current = resetTrace;
      setTimeLeft(ROUND_DURATION_SECONDS);
      setHasRoundStarted(false);
      lastCrashPointRef.current = null;
      setStatus("failed");
      return;
    }

    setStatus("playing");
  };

  useEffect(() => {
    if (!hasRoundStarted || status === "success" || status === "failed") {
      return;
    }

    const timerId = window.setInterval(() => {
      setTimeLeft((currentTimeLeft) => {
        if (currentTimeLeft <= 1) {
          window.clearInterval(timerId);
          pointerActiveRef.current = false;
          setStatus("failed");
          return 0;
        }

        return currentTimeLeft - 1;
      });
    }, 1000);

    return () => {
      window.clearInterval(timerId);
    };
  }, [hasRoundStarted, status]);

  const isPointerTouchingWall = (pointerPoint: Point) => {
    return wallLines.some((wallLine) => {
      const distanceToWall = getPointToSegmentDistance(
        pointerPoint,
        { x: wallLine.x1, y: wallLine.y1 },
        { x: wallLine.x2, y: wallLine.y2 },
      );

      return distanceToWall <= wallCollisionRadius;
    });
  };

  const evaluatePointerPath = (pointerPoint: Point, currentTrace: Cell[]) => {
    const lastTraceCell = currentTrace[currentTrace.length - 1];
    if (!lastTraceCell) {
      return {
        reachedExit: false,
        nextTrace: currentTrace,
      };
    }

    const neighborCells = getConnectedNeighborCells(
      mazeBlueprint.grid,
      lastTraceCell,
    );
    const commitRadius =
      Math.min(cellSizeRef.current.width, cellSizeRef.current.height) *
      POINTER_CELL_COMMIT_RADIUS_MULTIPLIER;

    let selectedNeighbor: Cell | null = null;
    let selectedDistance = Number.POSITIVE_INFINITY;

    for (const neighborCell of neighborCells) {
      const neighborCenter = getCellCenter(
        neighborCell.row,
        neighborCell.col,
        cellSizeRef.current.width,
        cellSizeRef.current.height,
      );
      const distanceToNeighborCenter = getDistance(
        pointerPoint,
        neighborCenter,
      );

      if (distanceToNeighborCenter > commitRadius) {
        continue;
      }

      if (distanceToNeighborCenter < selectedDistance) {
        selectedDistance = distanceToNeighborCenter;
        selectedNeighbor = neighborCell;
      }
    }

    if (!selectedNeighbor) {
      return {
        reachedExit: false,
        nextTrace: currentTrace,
      };
    }

    const committedNeighbor = selectedNeighbor;

    const nextTrace = [...currentTrace];
    const previousTraceCell = nextTrace[nextTrace.length - 2] ?? null;

    if (previousTraceCell && isSameCell(previousTraceCell, committedNeighbor)) {
      nextTrace.pop();
    } else {
      const existingIndex = nextTrace.findIndex((cell) =>
        isSameCell(cell, committedNeighbor),
      );

      if (existingIndex >= 0) {
        nextTrace.splice(existingIndex + 1);
      } else {
        nextTrace.push(committedNeighbor);
      }
    }

    const activeCell = nextTrace[nextTrace.length - 1];

    return {
      reachedExit: isSameCell(activeCell, mazeBlueprint.exitCell),
      nextTrace,
    };
  };

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (
      status === "success" ||
      status === "failed" ||
      crashCount >= MAX_CRASHES
    ) {
      return;
    }

    const mazeElement = mazeRef.current;
    if (!mazeElement) return;

    const pointerPoint = getPointerPosition(event, mazeElement, mazeSize);
    const reachRadius =
      Math.min(cellSizeRef.current.width, cellSizeRef.current.height) *
      POINTER_REACH_RADIUS_MULTIPLIER;
    const crashRejoinRadius = reachRadius * CRASH_REJOIN_RADIUS_MULTIPLIER;
    const currentTrace = traceCellsRef.current;
    const canResumeFromCrashPoint =
      lastCrashPointRef.current !== null &&
      getDistance(pointerPoint, lastCrashPointRef.current) <= crashRejoinRadius;

    if (currentTrace.length === 0) {
      const canStartFromEntry =
        getDistance(pointerPoint, startPoint) <= reachRadius * 1.65;
      if (!canStartFromEntry) {
        return;
      }

      const startTrace = [mazeBlueprint.entryCell];
      setTraceCells(startTrace);
      traceCellsRef.current = startTrace;
    } else {
      let nearestTraceIndex = -1;
      let nearestDistance = Number.POSITIVE_INFINITY;

      currentTrace.forEach((cell, index) => {
        const tracePoint = getCellCenter(
          cell.row,
          cell.col,
          cellSizeRef.current.width,
          cellSizeRef.current.height,
        );
        const distanceToTracePoint = getDistance(pointerPoint, tracePoint);

        if (
          distanceToTracePoint <= reachRadius &&
          distanceToTracePoint < nearestDistance
        ) {
          nearestDistance = distanceToTracePoint;
          nearestTraceIndex = index;
        }
      });

      if (nearestTraceIndex < 0) {
        if (canResumeFromCrashPoint) {
          pointerActiveRef.current = true;
          setHasRoundStarted(true);
          setStatus("playing");
          lastCrashPointRef.current = null;

          event.preventDefault();
          event.currentTarget.setPointerCapture(event.pointerId);
        }
        return;
      }

      if (nearestTraceIndex < currentTrace.length - 1) {
        const trimmedTrace = currentTrace.slice(0, nearestTraceIndex + 1);
        setTraceCells(trimmedTrace);
        traceCellsRef.current = trimmedTrace;
      }
    }

    pointerActiveRef.current = true;
    setHasRoundStarted(true);
    setStatus("playing");
    lastCrashPointRef.current = null;

    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!pointerActiveRef.current || status !== "playing") return;

    const mazeElement = mazeRef.current;
    if (!mazeElement) return;

    const pointerPoint = getPointerPosition(event, mazeElement, mazeSize);

    if (isPointerTouchingWall(pointerPoint)) {
      registerCrash(pointerPoint);
      return;
    }

    const currentTrace = traceCellsRef.current;
    const evaluation = evaluatePointerPath(pointerPoint, currentTrace);

    if (evaluation.nextTrace !== currentTrace) {
      setTraceCells(evaluation.nextTrace);
      traceCellsRef.current = evaluation.nextTrace;
    }

    if (evaluation.reachedExit) {
      markSuccess();
    }
  };

  const handlePointerEnd = (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }

    pointerActiveRef.current = false;
  };

  const resultMessage =
    status === "success"
      ? "PARABÉNS"
      : status === "failed"
        ? "TENTE NOVAMENTE"
        : "";

  if (!isTablet()) {
    return (
      <div className="unsupported-device">
        <button
          className="back-button"
          onClick={onBackToHome}
          title="Voltar ao início">
          ← Voltar
        </button>
        Este jogo está disponível apenas em tablets.
        <br />
        Aceda através de um dispositivo compatível ou utilize a emulação de
        dispositivo no navegador.
      </div>
    );
  }

  return (
    <div className="labirinto-equilibrio">
      <button
        className="back-button"
        onClick={onBackToHome}
        title="Voltar ao início">
        ← Voltar
      </button>

      <div className="labirinto-shell">
        <header className="labirinto-header">
          <img
            src="/images/joga-e-ganha-transparent.png"
            alt="Logo Joga e Ganha"
            className="labirinto-top-logo"
          />
        </header>

        <section className="labirinto-prize">
          <div className="labirinto-hud-item">Tempo: {timeLeft}s</div>
          <img
            src={PRIZE_IMAGE_PATH}
            alt="Produto prémio"
            className="labirinto-prize-image"
          />
          <div className="labirinto-hud-item">
            Colisões: {crashCount}/{MAX_CRASHES}
          </div>
        </section>

        <section className="labirinto-maze-wrapper">
          <div className="labirinto-maze-frame">
            <div
              className="labirinto-maze"
              ref={mazeRef}
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerEnd}
              onPointerCancel={handlePointerEnd}
              role="application"
              aria-label="Labirinto do Equilíbrio">
              <svg
                viewBox={`0 0 ${mazeSize.width} ${mazeSize.height}`}
                className="labirinto-grid"
                preserveAspectRatio="none"
                aria-hidden="true">
                {wallLines.map((wallLine) => (
                  <line
                    key={wallLine.key}
                    className="labirinto-wall-line"
                    strokeWidth={wallStrokeWidth}
                    x1={wallLine.x1}
                    y1={wallLine.y1}
                    x2={wallLine.x2}
                    y2={wallLine.y2}
                  />
                ))}
              </svg>

              <svg
                viewBox={`0 0 ${mazeSize.width} ${mazeSize.height}`}
                className="labirinto-trace-svg"
                preserveAspectRatio="none">
                {tracePoints.length > 1 ? (
                  <polyline
                    points={tracePoints
                      .map((point) => `${point.x},${point.y}`)
                      .join(" ")}
                    strokeWidth={traceStrokeWidth}
                    className={`labirinto-trace-line ${
                      isCrashFlashing ? "flash" : ""
                    }`}
                  />
                ) : null}
              </svg>
            </div>
          </div>

          <div className="labirinto-result-slot" aria-live="polite">
            {resultMessage ? (
              <div className={`labirinto-result-text ${status}`}>
                {resultMessage}
              </div>
            ) : null}
          </div>

          <div
            className="labirinto-reset-slot"
            aria-hidden={status !== "failed" && status !== "success"}>
            <button
              type="button"
              className={`labirinto-reset ${
                status === "failed" || status === "success" ? "" : "hidden"
              }`}
              onClick={resetAttempt}
              disabled={status !== "failed" && status !== "success"}>
              Jogar novamente
            </button>
          </div>
        </section>

        <footer className="labirinto-footer">
          <img
            src="/images/continente-footer.png"
            alt="Logo Continente"
            className="labirinto-bottom-logo"
          />
        </footer>
      </div>
    </div>
  );
}
